/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import ConfirmationModal from '@salesforce/retail-react-app/app/components/confirmation-modal'
import {defineMessage} from 'react-intl'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useUnavailableProductsInCartContext} from '../../contexts/unavailable-products-in-cart-context'

// Custom dialog config with proper defineMessage calls
const REMOVE_UNAVAILABLE_CART_ITEM_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Items Unavailable',
        id: 'confirmation_modal.remove_cart_item.title.items_unavailable'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Some items in your cart are no longer available and need to be removed to continue.',
        id: 'confirmation_modal.remove_cart_item.message.need_to_remove_due_to_unavailability'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Remove Items',
        id: 'confirmation_modal.remove_cart_item.action.remove'
    }),
    ariaLabel: defineMessage({
        defaultMessage: 'Remove unavailable items from cart',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.remove'
    })
}

/**
 * This Component determines if the provided products have become unavailable or out of stock or low stock that
 * can't be fulfilled and will prompt the users to remove them before proceeding any further
 *
 * @param productIds -  list of product ids to check for availability. This prop will be deprecated in the upcoming release.
 * Please use productItems prop
 * @param productItems -  basket product items. This will be ignored if productIds is passed
 * @param handleUnavailableProducts - callback function to handle what to do with unavailable products
 * @returns {JSX.Element} -  Conformation Modal Component
 *
 */
const UnavailableProductConfirmationModal = ({handleUnavailableProducts = noop}) => {
    const {UnavailableProductsInCart, unavailableProductIdsRef} =
        useUnavailableProductsInCartContext()

    return (
        <ConfirmationModal
            data-testid="unavailable-product-modal"
            closeOnEsc={false}
            closeOnOverlayClick={false}
            {...REMOVE_UNAVAILABLE_CART_ITEM_DIALOG_CONFIG}
            hideAlternateAction={true}
            onPrimaryAction={async () => {
                await handleUnavailableProducts(unavailableProductIdsRef.current)
                unavailableProductIdsRef.current = null
                UnavailableProductsInCart.onClose()
            }}
            onAlternateAction={() => {}}
            isOpen={UnavailableProductsInCart.isOpen}
            onClose={() => {}}
            onOpen={() => {}} // dumb onOpen prop to satisfy prop validation
        />
    )
}

UnavailableProductConfirmationModal.propTypes = {
    handleUnavailableProducts: PropTypes.func
}

export default UnavailableProductConfirmationModal
